import React from 'react';
import type { Theme } from 'baseui';
import { useStyletron } from 'baseui';
import { type OnChangeParams, type Option, Select, SIZE as SELECT_SIZE } from 'baseui/select';
import { LabelXSmall } from 'baseui/typography';
import { DEFAULT_SPEED_OPTIONS } from './constants';
import type { SpeedSelectProps } from './types';
const SELECT_MIN_WIDTH = '52px';
const getSelectOverrides = {
  Root: {
    style: ({
      $theme
    }: {
      $theme: Theme;
    }) => ({
      backgroundColor: $theme.colors.backgroundSecondary,
      borderRadius: $theme.borders.radius300,
      minWidth: SELECT_MIN_WIDTH,
      height: $theme.sizing.scale900,
      display: 'flex',
      alignItems: 'center'
    })
  },
  ControlContainer: {
    style: () => ({
      borderWidth: '0px',
      backgroundColor: 'transparent',
      minHeight: '0px',
      display: 'flex',
      alignItems: 'center'
    })
  },
  ValueContainer: {
    style: ({
      $theme
    }: {
      $theme: Theme;
    }) => ({
      ...$theme.typography.LabelSmall,
      paddingTop: '0px',
      paddingBottom: '0px',
      paddingLeft: $theme.sizing.scale300,
      color: $theme.colors.contentTertiary,
      display: 'flex',
      alignItems: 'center'
    })
  },
  IconsContainer: {
    style: ({
      $theme
    }: {
      $theme: Theme;
    }) => ({
      paddingRight: $theme.sizing.scale300,
      display: 'flex',
      alignItems: 'center'
    })
  },
  SelectArrow: {
    props: {
      overrides: {
        Svg: {
          style: ({
            $theme
          }: {
            $theme: Theme;
          }) => ({
            color: $theme.colors.contentTertiary,
            width: $theme.sizing.scale400,
            height: $theme.sizing.scale400
          })
        }
      }
    }
  },
  DropdownListItem: {
    style: ({
      $theme
    }: {
      $theme: Theme;
    }) => ({
      ...$theme.typography.LabelSmall,
      color: $theme.colors.contentTertiary,
      padding: $theme.sizing.scale300,
      borderBottom: `1px solid ${$theme.colors.borderOpaque}`,
      ':last-child': {
        borderBottom: 'none'
      },
      ':hover': {
        backgroundColor: $theme.colors.backgroundTertiary
      }
    })
  },
  Dropdown: {
    style: ({
      $theme
    }: {
      $theme: Theme;
    }) => ({
      backgroundColor: $theme.colors.backgroundSecondary,
      borderRadius: $theme.borders.radius300,
      padding: '0px',
      boxShadow: $theme.lighting.shadow600
    })
  },
  Popover: {
    props: {
      overrides: {
        Body: {
          style: ({
            $theme
          }: {
            $theme: Theme;
          }) => ({
            backgroundColor: 'transparent',
            borderRadius: $theme.borders.radius300,
            boxShadow: 'none'
          })
        },
        Inner: {
          style: ({
            $theme
          }: {
            $theme: Theme;
          }) => ({
            backgroundColor: $theme.colors.backgroundSecondary,
            borderRadius: $theme.borders.radius300
          })
        }
      }
    }
  },
  OptionContent: {
    style: ({
      $theme
    }: {
      $theme: Theme;
    }) => ({
      ...$theme.typography.LabelSmall,
      color: $theme.colors.contentTertiary
    })
  }
};
export const SpeedSelect = ({
  speed,
  onSpeedChange,
  options = DEFAULT_SPEED_OPTIONS
}: SpeedSelectProps) => {
  const [css, theme] = useStyletron();
  const selectOptions: Option[] = options.map(s => ({
    label: `${s}x`,
    id: s
  }));
  const selectedValue: Option[] = [{
    label: `${speed}x`,
    id: speed
  }];
  const handleChange = (params: OnChangeParams) => {
    const selected = (params.value as Option[])?.[0];
    if (selected?.id !== undefined) {
      onSpeedChange(Number(selected.id));
    }
  };
  return <div className={css({
    display: 'flex',
    alignItems: 'center',
    gap: theme.sizing.scale300
  })}>
      <LabelXSmall>Speed</LabelXSmall>
      <Select size={SELECT_SIZE.mini} options={selectOptions} value={selectedValue} onChange={handleChange} overrides={getSelectOverrides} clearable={false} searchable={false} />
    </div>;
};